import { forwardRef } from "react";
import { Sparkles } from "lucide-react";
import MessageBubble from "@/components/MessageBubble";
import AiResearchCard from "@/components/chat/AiResearchCard";
import StreamingAiBubble from "@/components/chat/StreamingAiBubble";
import MeetingSummaryCard from "@/components/chat/MeetingSummaryCard";
import MeetingDigestCard from "@/components/chat/MeetingDigestCard";

/** Centered Today/Yesterday/date pill between message clusters. */
function DaySeparator({ label }) {
        return (
                <div className="flex justify-center my-3">
                        <span className="bg-surface-2 text-ink-mute text-[11px] font-medium px-2.5 py-1 rounded-full">
                                {label}
                        </span>
                </div>
        );
}

function labelForDay(d) {
        const today = new Date();
        const yest = new Date();
        yest.setDate(today.getDate() - 1);
        if (d.toDateString() === today.toDateString()) return "Today";
        if (d.toDateString() === yest.toDateString()) return "Yesterday";
        return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

/**
 * MessageList — vertically scrollable message stream with author-clustering
 * and day separators. Pure presentational; receives the full ordered list.
 */
const MessageList = forwardRef(function MessageList(
        { messages, memberMap, userId, typingUsers, onOpenThread, onCreateTask, onPickIdea, topSlot, bottomSlot, comparisonAllowed = true, onFollowUp, onRouteChoice, onAiAction, onReply, pendingAI = false, onStopAI, view = "combined", discussions = [], onOpenDiscussion, readState = {}, chatType = "group", canCreateTasks = true },
        scrollRef,
) {
        const isHuman = view === "human";
        const members = Object.values(memberMap || {});
        const threadById = {};
        for (const d of discussions) threadById[d.id] = d;
        // Discussions started from a specific human message → "AI Research: N".
        const linkedByMsg = {};
        for (const d of discussions) {
                if (d.linked_human_message_id) {
                        (linkedByMsg[d.linked_human_message_id] =
                                linkedByMsg[d.linked_human_message_id] || []).push(d);
                }
        }
        // Human view: drop AI question/answer bubbles and surface each AI
        // discussion as a single compact card (in place, so chronology holds).
        // Combined view: current behaviour (hide only the echoed ai_question).
        const seenThreads = new Set();
        const visible = [];
        for (const m of messages) {
                if (m.message_type === "ai_question") {
                        // While a multi-model @ai is still running, stream its per-model
                        // answers in place via a StreamingAiBubble until the final
                        // synthesized answer lands (then it's replaced by that answer).
                        const tid = m.metadata?.thread_id;
                        const answered =
                                tid && messages.some(
                                        (x) => x.message_type === "ai_answer" && x.metadata?.thread_id === tid && !x.deleted_at,
                                );
                        const partials = Array.isArray(m.metadata?.partials) ? m.metadata.partials : [];
                        if (!isHuman && !m.deleted_at && !answered && partials.length > 0) {
                                visible.push({ __streaming: true, id: `stream-${m.id}`, created_at: m.created_at, sender_id: "ai-system", _msg: m });
                        }
                        continue;
                }
                if (isHuman && m.message_type === "ai_answer") {
                        const tid = m.metadata?.thread_id;
                        if (tid && !seenThreads.has(tid)) {
                                seenThreads.add(tid);
                                visible.push({ __card: true, id: `card-${tid}`, thread_id: tid, created_at: m.created_at, sender_id: `card-${tid}` });
                        }
                        continue;
                }
                visible.push(m);
        }
        // Index every message by id so replies can render a quoted preview of
        // their parent (resolved client-side from the loaded list).
        const msgById = {};
        for (const m of messages) msgById[m.id] = m;
        const out = [];
        let lastDayKey = null;
        let prevSender = null;

        for (let i = 0; i < visible.length; i++) {
                const m = visible[i];
                const d = new Date(m.created_at);
                const dayKey = d.toDateString();

                if (dayKey !== lastDayKey) {
                        out.push(<DaySeparator key={`day-${dayKey}`} label={labelForDay(d)} />);
                        lastDayKey = dayKey;
                        prevSender = null;
                }

                const nextMsg = visible[i + 1];
                const sameNextDay =
                        nextMsg && new Date(nextMsg.created_at).toDateString() === dayKey;
                const sameNextSender =
                        sameNextDay && nextMsg && nextMsg.sender_id === m.sender_id;

                const showAvatar = prevSender !== m.sender_id;
                const showTimestamp = !sameNextSender;
                prevSender = m.sender_id;

                if (m.__streaming) {
                        out.push(<StreamingAiBubble key={m.id} message={m._msg} />);
                        prevSender = null;
                        continue;
                }

                if (m.__card) {
                        out.push(
                                <AiResearchCard
                                        key={m.id}
                                        discussion={threadById[m.thread_id] || { id: m.thread_id, title: "AI research" }}
                                        onOpen={onOpenDiscussion}
                                />,
                        );
                        prevSender = null;
                        continue;
                }

                if (m.message_type === "meeting_summary") {
                        out.push(<MeetingSummaryCard key={m.id} message={m} />);
                        prevSender = null;
                        continue;
                }

                if (m.message_type === "meeting_digest") {
                        out.push(<MeetingDigestCard key={m.id} message={m} myId={userId} />);
                        prevSender = null;
                        continue;
                }

                // Quoted-reply preview: only user text replies carry a visible
                // quote (AI answers / task confirmations use parent_message_id
                // internally and shouldn't render a quote bubble).
                let parentPreview = null;
                if (m.message_type === "text" && m.parent_message_id && msgById[m.parent_message_id]) {
                        const p = msgById[m.parent_message_id];
                        const pName =
                                p.sender_id === userId
                                        ? "You"
                                        : (typeof p.sender_id === "string" && p.sender_id.startsWith("ai"))
                                                ? "AI"
                                                : memberMap[p.sender_id]?.name || "Member";
                        parentPreview = {
                                name: pName,
                                body: (p.body || "").replace(/[*#`>]/g, "").slice(0, 120),
                        };
                }

                out.push(
                        <MessageBubble
                                key={m.id}
                                message={m}
                                sender={
                                        m.sender_id === "ai-system"
                                                ? { name: "AI System", avatar: null }
                                                : m.sender_id?.startsWith?.("ai-agent-")
                                                        ? { name: m.metadata?.role_label || "AI Agent", avatar: null, is_ai_agent: true }
                                                        : memberMap[m.sender_id] || { name: "Unknown" }
                                }
                                isMe={m.sender_id === userId}
                                showAvatar={showAvatar}
                                showTimestamp={showTimestamp}
                                onOpenThread={onOpenThread}
                                onCreateTask={onCreateTask}
                                canCreateTasks={canCreateTasks}
                                onPickIdea={onPickIdea}
                                comparisonAllowed={comparisonAllowed}
                                onFollowUp={onFollowUp}
                                onRouteChoice={onRouteChoice}
                                onAiAction={onAiAction}
                                onReply={onReply}
                                parentPreview={parentPreview}
                                chatType={chatType}
                                members={members}
                                readState={readState}
                                myId={userId}
                        />,
                );

                if (linkedByMsg[m.id]) {
                        const list = linkedByMsg[m.id];
                        out.push(
                                <div
                                        key={`ai-linked-${m.id}`}
                                        className={`flex ${m.sender_id === userId ? "justify-end" : "justify-start"} -mt-1`}
                                >
                                        <button
                                                type="button"
                                                data-testid={`ai-linked-indicator-${m.id}`}
                                                onClick={() => onOpenDiscussion?.(list[0].id)}
                                                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ai hover:bg-ai/15 bg-ai/10 px-2 py-0.5 rounded-full transition-colors"
                                        >
                                                <Sparkles className="w-3 h-3" /> AI Research: {list.length}
                                        </button>
                                </div>,
                        );
                }

                if (m.metadata?.ai_publication?.thread_id) {
                        const tid = m.metadata.ai_publication.thread_id;
                        out.push(
                                <div
                                        key={`ai-pub-${m.id}`}
                                        className={`flex ${m.sender_id === userId ? "justify-end" : "justify-start"} -mt-1`}
                                >
                                        <button
                                                type="button"
                                                data-testid={`ai-publication-open-${m.id}`}
                                                onClick={() => onOpenDiscussion?.(tid)}
                                                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ai hover:bg-ai/15 bg-ai/10 px-2 py-0.5 rounded-full transition-colors"
                                        >
                                                <Sparkles className="w-3 h-3" /> Open Full Research
                                        </button>
                                </div>,
                        );
                }
        }

        return (
                <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-3 md:px-6 py-4 space-y-2"
                        data-testid="messages-list"
                >
                        {topSlot}
                        {messages.length === 0 && (
                                <div className="text-center text-ink-dim py-12">
                                        <div className="text-base font-semibold mb-2 text-ink">
                                                Start the conversation
                                        </div>
                                        <div className="text-sm">
                                                Type a message or use{" "}
                                                <span className="text-ai">@AI</span> to query models.
                                        </div>
                                </div>
                        )}
                        {out}
                        {(() => {
                                const ids = Object.entries(typingUsers || {})
                                        .filter(([k, v]) => v && k !== userId)
                                        .map(([k]) => k);
                                if (ids.length === 0) return null;
                                const aiWorking = ids.some((id) => id.startsWith("ai-agent-") || id === "ai-system");
                                return (
                                        <div
                                                data-testid="typing-indicator"
                                                className={
                                                        "inline-flex items-center gap-2 mx-3 px-3 py-1.5 rounded-2xl rounded-bl-md text-xs " +
                                                        (aiWorking
                                                                ? "bg-amber-400/[0.08] text-amber-200 ring-1 ring-amber-400/20"
                                                                : "bg-surface-2 text-ink-mute ring-1 ring-hairline")
                                                }
                                        >
                                                <span className="flex gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                                                </span>
                                                {aiWorking ? (
                                                        <span>🎯 <span className="font-medium">@devmanager</span> is working…</span>
                                                ) : (
                                                        <span className="italic">Typing…</span>
                                                )}
                                        </div>
                                );
                        })()}
                        {pendingAI && !visible.some((v) => v.__streaming) && (
                                <div
                                        data-testid="ai-thinking-indicator"
                                        className="inline-flex items-center gap-2 mx-3 px-3 py-1.5 rounded-2xl rounded-bl-md text-xs bg-ai-tint text-ai ring-1 ring-ai/25"
                                >
                                        <span className="flex gap-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                                                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                                                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                                        </span>
                                        <span className="font-medium">AI is thinking…</span>
                                        <button
                                                type="button"
                                                data-testid="stop-ai-btn"
                                                onClick={onStopAI}
                                                className="ml-1 inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-tn-red/15 text-tn-red hover:bg-tn-red/25 font-semibold"
                                        >
                                                <span className="w-2 h-2 rounded-[2px] bg-current" />
                                                Stop
                                        </button>
                                </div>
                        )}
                        {bottomSlot}
                </div>
        );
});

export default MessageList;
